/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */

import { motion } from "framer-motion";
import Hls from "hls.js";
import type { ReactElement } from "react";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { useTransitionNavigation } from "../../shared/hooks/useTransitionNavigation";
import { pageTransitions } from "../../shared/transitions/pageTransitions";

interface SplashVideoProps {
  readonly videoRef: React.RefObject<HTMLVideoElement | null>;
  readonly videoError: boolean;
  readonly onVideoLoaded: () => void;
  readonly onVideoEnd: () => void;
  readonly onVideoError: () => void;
}

function SplashVideo({
  videoRef,
  videoError,
  onVideoLoaded,
  onVideoEnd,
  onVideoError,
}: SplashVideoProps): ReactElement {
  const handleLoadedData = () => {
    console.log("📊 Video metadata loaded:", {
      duration: videoRef.current?.duration,
      readyState: videoRef.current?.readyState,
      paused: videoRef.current?.paused,
      ended: videoRef.current?.ended,
      currentTime: videoRef.current?.currentTime,
    });

    // Explicitly start playback (autoplay might be blocked)
    if (videoRef.current?.paused) {
      console.log("▶️  Explicitly calling play() because video is paused");
      videoRef.current.play().catch((error) => {
        console.error("Failed to auto-play video:", error);
        // If autoplay fails, still show the video experience
      });
    }

    onVideoLoaded();
  };

  const handleEnded = () => {
    const duration = videoRef.current?.duration;
    const currentTime = videoRef.current?.currentTime;
    console.log("🏁 Video onEnded event", { duration, currentTime });

    // Ignore spurious 'ended' events if duration is invalid or video hasn't really played
    if (!duration || Number.isNaN(duration) || duration === 0 || (currentTime && currentTime < 1)) {
      console.warn("⚠️  Ignoring spurious ended event - video hasn't played yet");
      return;
    }

    onVideoEnd();
  };

  const handleError = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    console.error("💥 Video onError event:", e);
    onVideoError();
  };

  if (videoError) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-neutral-900 to-black">
        <div className="text-center space-y-4">
          <div className="text-4xl md:text-6xl font-bold text-white">Lumi</div>
          <div className="text-lg text-neutral-400">Streaming Platform</div>
        </div>
      </div>
    );
  }

  return (
    <video
      ref={videoRef}
      className="absolute inset-0 w-full h-full object-cover"
      autoPlay
      muted
      playsInline
      onLoadedData={handleLoadedData}
      onEnded={handleEnded}
      onError={handleError}
    />
  );
}

interface SplashOverlaysProps {
  readonly showTransition: boolean;
  readonly isVideoLoaded: boolean;
  readonly isVideoEnded: boolean;
  readonly videoError: boolean;
  readonly onSkip: () => void;
}

function SplashOverlays({
  showTransition,
  isVideoLoaded,
  isVideoEnded,
  videoError,
  onSkip,
}: SplashOverlaysProps): ReactElement {
  return (
    <>
      {/* Transition Overlay with blur effect */}
      <div
        className={`absolute inset-0 bg-black transition-all duration-1200 ease-out ${
          showTransition ? "opacity-80 backdrop-blur-md" : "opacity-0 backdrop-blur-0"
        }`}
      />

      {/* Additional fade overlay */}
      <div
        className={`absolute inset-0 bg-black transition-opacity duration-1200 ease-out ${
          showTransition ? "opacity-100" : "opacity-0"
        }`}
      />

      {/* Skip Button */}
      <button
        onClick={onSkip}
        className="absolute top-8 right-8 text-white/70 hover:text-white text-sm transition-colors duration-200 z-20 px-3 py-2 rounded-md bg-black/20 backdrop-blur-sm hover:bg-black/40"
        aria-label="Skip splash screen"
      >
        Skip
      </button>

      {/* Loading indicator */}
      {!isVideoLoaded && !videoError && (
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <div className="flex items-center space-x-2 text-white/70">
            <div className="w-2 h-2 bg-white/70 rounded-full animate-pulse"></div>
            <div className="w-2 h-2 bg-white/70 rounded-full animate-pulse" style={{ animationDelay: "0.2s" }}></div>
            <div className="w-2 h-2 bg-white/70 rounded-full animate-pulse" style={{ animationDelay: "0.4s" }}></div>
          </div>
        </div>
      )}

      {/* Progress indicator when video is playing */}
      {isVideoLoaded && !isVideoEnded && !videoError && (
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <div className="text-white/50 text-xs">Loading experience...</div>
        </div>
      )}
    </>
  );
}

export function SplashPage(): ReactElement {
  const navigate = useNavigate();
  const { user } = useAuth();
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isVideoEnded, setIsVideoEnded] = useState(false);
  const [videoError, setVideoError] = useState(false);

  // Use transition navigation for smooth Splash → SignIn transition
  const { isTransitioning, transitionTo } = useTransitionNavigation({
    onTransitionStart: () => console.log("🎬 Starting Splash → SignIn transition"),
    onNavigationComplete: () => console.log("✅ Navigated to SignIn"),
  });

  // Unlock any existing orientation lock from previous pages (e.g., Player)
  useEffect(() => {
    const screenOrientation = screen?.orientation as { unlock?: () => void } | undefined;
    if (screenOrientation?.unlock) {
      screenOrientation.unlock();
      console.debug("[Splash] Unlocked orientation from previous page");
    }
  }, []);

  const handleVideoLoaded = (): void => {
    console.log("📹 Video loaded event fired");
    setIsVideoLoaded(true);
  };

  const handleVideoEnd = () => {
    console.log("🎬 Splash video ended, navigating to sign in");
    setIsVideoEnded(true);
    transitionTo("/sign-in", pageTransitions.cinematicFade);
  };

  const handleVideoError = (): void => {
    console.warn("❌ Splash video failed to load, proceeding to sign-in");
    console.log("Video error details:", {
      videoSrc: "https://videos.lumi.theforge.space/lumi-bumper/master.m3u8",
      hlsSupported: Hls.isSupported(),
      nativeHlsSupported: videoRef.current?.canPlayType("application/vnd.apple.mpegurl") || "unknown",
    });
    setVideoError(true);
    setTimeout(() => {
      handleTransition();
    }, 2000);
  };

  const handleTransition = (): void => {
    console.log("🚀 handleTransition called");
    // Use cinematicFade for dramatic Splash → SignIn transition
    transitionTo("/sign-in", pageTransitions.cinematicFade);
  };

  const handleSkip = (): void => {
    console.log("⏭️  Skip button clicked");
    handleTransition();
  };

  /**
   * Setup HLS event handlers
   */
  const setupHlsHandlers = (hls: Hls, video: HTMLVideoElement): void => {
    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      console.log("✅ HLS manifest loaded successfully");
      setIsVideoLoaded(true);

      // Explicitly start playback after manifest is loaded
      if (video.paused) {
        console.log("▶️  Starting HLS playback");
        video.play().catch((error) => {
          console.error("Failed to auto-play HLS video:", error);
        });
      }
    });

    hls.on(Hls.Events.ERROR, (event, data) => {
      console.error("HLS error:", data);
      if (data.fatal) {
        handleVideoError();
      }
    });
  };

  /**
   * Initialize video with HLS or native support
   */
  const initializeVideo = (): void => {
    const video = videoRef.current;
    if (!video) return;

    const videoSrc = "https://videos.lumi.theforge.space/lumi-bumper/master.m3u8";

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = videoSrc;
      console.log("Using native HLS support");
    } else if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
      });

      hlsRef.current = hls;
      hls.loadSource(videoSrc);
      hls.attachMedia(video);
      setupHlsHandlers(hls, video);

      console.log("Using HLS.js for playback");
    } else {
      console.error("HLS not supported in this browser");
      handleVideoError();
    }
  };

  useEffect(() => {
    if (user) {
      navigate("/home", { replace: true });
      return;
    }

    initializeVideo();

    const maxWaitTimer = setTimeout(() => {
      console.log("⏰ Max wait timer (10s) expired, transitioning");
      handleTransition();
    }, 10000);

    return () => {
      clearTimeout(maxWaitTimer);
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [user, navigate]);

  return (
    <motion.div
      className="relative h-screen bg-black overflow-hidden"
      initial="initial"
      animate={isTransitioning ? "exit" : "animate"}
      exit="exit"
      variants={pageTransitions.cinematicFade}
    >
      <SplashVideo
        videoRef={videoRef}
        videoError={videoError}
        onVideoLoaded={handleVideoLoaded}
        onVideoEnd={handleVideoEnd}
        onVideoError={handleVideoError}
      />
      <SplashOverlays
        showTransition={isTransitioning}
        isVideoLoaded={isVideoLoaded}
        isVideoEnded={isVideoEnded}
        videoError={videoError}
        onSkip={handleSkip}
      />
    </motion.div>
  );
}
